import Moment from 'moment';
import React from 'react';
import CustomScroll from 'react-custom-scroll';
import { FormattedDate, FormattedTime } from 'react-intl';
import { getCalendarFreeIntervals } from 'src/services/api/calendar';
import { getTime, isValidTime, setTimeOnDate, getDate, getOffsetString, } from 'src/services/dateTime';
import { IDateTimeSelectBase } from '../DateTimeSelect';
import { Bone } from 'src/components/Placeholder/Placeholder';
import AvailabilityWarning from 'src/components/AvailabilityWarning';
import { getServiceFreeIntervals } from 'src/services/api/service';
import { useSubscription } from 'src/hooks';
import { useState, useEffect } from 'react';
import Select, { SelectSkeleton } from 'src/components/form-components/Select/Select';
import Radio from 'src/components/form-components/Radio/Radio';
import { scrollIntoView } from 'src/services/scrollerFunctions';
import { AppConsumer, IAppProvidedState } from 'src/contexts/AppContext';

interface ITimePickerProps extends IDateTimeSelectBase {
    selectedDate: string;
    value?: string;
    onChange: (selectedTime?: string, clicked?: boolean) => void;
    formatTime: (value: string | number | Date) => string;
    calendarChanged?: boolean;
    clickedByUser?: boolean;
}

const TimePicker: React.FunctionComponent<ITimePickerProps> = ({ calendarChanged, allowAlreadySelected, isOnlyService, calendar, service, personCount, selectedDate, value, pendingReservations, modifiedReservationId, onChange, formatTime, autoSelect, clickedByUser }) => {

    const queryParams = {
        personCount,
        startDate: selectedDate,
        endDate: selectedDate,
        pendingReservations,
        modifiedReservationId
    };

    const fetchFreeTimes = React.useCallback(async () => {

        if (!service || !selectedDate) { return []; }

        const serviceId = service.Id;

        const getIntervals = () => !isOnlyService && calendar
            ? getCalendarFreeIntervals({ calendarId: calendar.Id, serviceId, ...queryParams })
            : getServiceFreeIntervals({ serviceId, ...queryParams })

        return getIntervals().then((dateInterval) =>
            dateInterval && dateInterval[0] && dateInterval[0].Intervals.map(interval => interval.Start) || []);

    }, [JSON.stringify(queryParams), selectedDate, calendar && calendar.Id, service && service.Id]);

    const [freeTimes, isLoading] = useSubscription(fetchFreeTimes, []);
    const [hasAutoSelected, setHasAutoSelected] = useState(false);

    const scrollToSelectedChip = () => {
        const selectedChip = document.querySelector('.chip__label--selected');
        const scrollParent = document.querySelector('.date-time-select__time-picker .rcs-inner-container');
        if (selectedChip && scrollParent) {
            scrollIntoView(scrollParent, selectedChip);
        }
    }

    useEffect(() => {
        if (!isLoading && !hasAutoSelected) {
            if (!freeTimes.length) {
                return;
            }
            if (autoSelect && isValidTime(autoSelect, freeTimes)) {
                onChange(getTime(autoSelect));
                setHasAutoSelected(true);
            }
            else if (!value || (value && !(freeTimes.includes(value))) || (value && calendarChanged && !clickedByUser)) {
                onChange(freeTimes[0]);
                setHasAutoSelected(false);
            }
        }

        scrollToSelectedChip();

    }, [hasAutoSelected, autoSelect, freeTimes, isLoading]);

    if (!service || !selectedDate) {
        return null;
    }

    const addOriginalTime = (times: string[]) => {
        if (calendar?.IsTimeSelectDropDown && times[0] !== '') {
            times.unshift('');
        }

        if (allowAlreadySelected && autoSelect && Moment(autoSelect).isSame(selectedDate, 'day')) {
            const autoSelectedTime = getTime(autoSelect);
            if (!times.includes(autoSelectedTime)) {
                times.push(autoSelectedTime);
                times.sort();
            }
        }

        return times;
    }

    const options = addOriginalTime(freeTimes);

    const TimePickerWidget = (() => {

        if (isLoading) {
            return calendar?.IsTimeSelectDropDown
                ? <div className="select--timepicker date-time-select__dropdown"><SelectSkeleton /></div>
                : <ChipGroupSkeleton />
        }

        if (!options || !options.length) {
            return <AvailabilityWarning type="noFreeIntervalsOnDay" modifier="color-red" />
        }

        if (calendar?.IsTimeSelectDropDown) {
            const handleSelectChange = (time: string) => {
                onChange(time);
            }

            return (
                <Select
                    className="select--timepicker date-time-select__dropdown"
                    values={
                        options.map((time) => {
                            return {
                                value: time,
                                label: time
                                    ? formatTime(setTimeOnDate(selectedDate, time))
                                    : ''
                            }
                        })}
                    value={value || ''}
                    onChange={handleSelectChange}
                />);
        }

        return (
            <CustomScroll allowOuterScroll={true}>
                <Radio<string>
                    options={options}
                    selected={value}
                    getLabel={(time, checked) =>
                        <div className={`chip__label ${checked ? 'chip__label--selected' : ''}`}>
                            <FormattedTime value={setTimeOnDate(selectedDate, time)} />
                        </div>}
                    onChange={(time) => onChange(time, true)}
                    name="time"
                    rootClassName="chip-group"
                    buttonClassName="chip"
                />
            </CustomScroll>
        )
    })();

    const dateTime = value ? setTimeOnDate(selectedDate, value) : getDate(selectedDate);
    const hasSelectedValue = selectedDate && value;
    const shouldDisplayEndTime = hasSelectedValue && service.IsDurationVisible && service.DurationToUser > 0;

    return (
        <div className="date-time-select__time-picker">
            <time dateTime={dateTime} className="date-time-select__date-time">
                <FormattedDate
                    value={selectedDate}
                    year="numeric"
                    month="long"
                    day="numeric"
                    weekday="long"
                />
                {hasSelectedValue && <>, <FormattedTime value={dateTime} /></>}
                {shouldDisplayEndTime && <>–<FormattedTime value={Moment(dateTime).add(service.DurationToUser, 'minutes').toDate()} /></>}
                <AppConsumer>
                    {(context: IAppProvidedState) => context.company ? getOffsetString(context.company.TimeZone) : null}
                </AppConsumer>
            </time>
            {TimePickerWidget}
        </div>
    )
}

const ChipGroupSkeleton = () =>
    <div className="chip-group" role="presentation">
        {Array.from({ length: 12 }).fill(0).map((_, i) => <div key={i} className="chip"><Bone className="chip__label"><FormattedTime value={0} /></Bone></div>)}
    </div>


export default TimePicker;